import { DataSourceType } from '@/modules/core/app/constants/datasource.constants';
import { BaseModel } from '@/modules/core/app/models/abstracts/BaseModel';

export class DataSource extends BaseModel {
  /**
   * @var {string}
   */
  id;

  /**
   * @var {string}
   */
  name;

  /**
   * @var {string}
   */
  type;

  /**
   * @var {string}
   */
  color;

  /**
   * @var {string}
   */
  custom_color;

  /**
   * @var {string}
   */
  icon;

  /**
   * @var {string}
   */
  custom_icon;

  /**
   * @var {string}
   */
  service_icon;

  /**
   * @var {string}
   */
  data_view;

  /**
   * @var {string}
   */
  id_name;

  /**
   * @var {string}
   */
  data_view_name;

  /**
   * @var {boolean}
   */
  has_custom_icon;

  /**
   * @var {boolean}
   */
  requires_group_by;

  /**
   * @var {boolean}
   */
  requires_date_range;

  /**
   * @var {boolean}
   */
  is_of_type_service;

  /**
   * @var {boolean}
   */
  include_num_entities;

  /**
   * @var {boolean}
   */
  include_data_freshness_date;

  /**
   * @var {boolean}
   */
  is_custom_service;

  /**
   * @var {boolean}
   */
  has_live_integration;

  /**
   * @var {boolean}
   */
  active;

  /**
   * @var {boolean}
   */
  has_stored_data;

  /**
   * @var {int}
   */
  live_assignments_threshold;

  /**
   * @var {boolean}
   */
  live_advanced_calculation_enabled;

  /**
   * @var {Object}
   */
  services;

  /**
   * @var {string}
   */
  uniqueKey;

  /**
   * @var {string}
   */
  primary_date_field;

  /**
   * @var {string}
   */
  connector_marketplace_url;

  constructor(model = {}) {
    super(model);

    this.id = model.id;
    this.name = model.name || model.text;
    this.type = model.type;
    this.color = model.color;
    this.custom_color = model.custom_color;
    this.icon = model.icon;
    this.custom_icon = model.custom_icon;
    this.data_view = model.data_view;
    this.id_name = model.id_name;
    this.data_view_name = model.data_view_name;
    // eslint-disable-next-line tap/no-raw-text-js
    this.icon = model.icon === 'icomoon-bar-chart' ? 'ti ti-default' : model.icon;
    this.service_icon = model.service_icon;
    this.has_custom_icon = model.has_custom_icon;
    this.requires_group_by = model.requires_group_by;
    this.requires_date_range = model.requires_date_range;
    this.is_of_type_service = model.is_of_type_service;
    this.include_num_entities = model.include_num_entities;
    this.include_data_freshness_date = model.include_data_freshness_date;
    this.is_custom_service = model.is_custom_service;
    this.has_live_integration = [true, '1', 1].includes(model.has_live_integration);
    this.geo_columns = model.geo_columns || {};
    this.mapbox_config = model.mapbox_config || {};
    this.active = model.active;
    this.has_stored_data = model.has_stored_data;
    this.live_assignments_threshold = model.live_assignments_threshold;
    this.live_advanced_calculation_enabled = model.live_advanced_calculation_enabled;
    this.services = model.services || {};
    this.uniqueKey = model.uniqueKey;
    this.primary_date_field = model.primary_date_field;
    this.connector_marketplace_url = model.connector_marketplace_url || '';
  }

  getIcon() {
    let icon = this.service_icon;

    if (this.custom_icon) {
      icon = this.custom_icon;
      if (!this.custom_icon.startsWith('custom-icon')) {
        icon = `custom-icon ${this.custom_icon}`;
      }
    } else if (this.icon) {
      // eslint-disable-next-line tap/no-raw-text-js
      if (
        this.icon.startsWith('icon-') ||
        this.icon.startsWith('ti-') ||
        this.icon.startsWith('ti ti-')
      ) {
        return this.icon;
      }
      return `serviceicon-${this.icon}`;
    } else if (this.type === DataSourceType.CATEGORY_DATA) {
      return icon;
    }

    if (this.service_icon && !this.service_icon?.startsWith('icon')) {
      icon = `serviceicon-${this.service_icon}`;
    }
    return icon;
  }

  /**
   * @returns {boolean}
   */
  isServiceData() {
    return this.type === DataSourceType.SERVICE_DATA;
  }

  /**
   * @returns {boolean}
   */
  isCategoryData() {
    return this.type === DataSourceType.CATEGORY_DATA;
  }

  isSeoWebsiteData() {
    return this.type === DataSourceType.SEO_WEBSITES;
  }

  /**
   * @returns {string}
   */
  getColor() {
    return this.type === DataSourceType.SERVICE_DATA && this.custom_color
      ? this.custom_color
      : this.color;
  }

  /**
   * @returns {string}
   */
  getName() {
    return this.id_name || this.name;
  }

  isIODataSourceType() {
    return [
      DataSourceType.ORDER,
      DataSourceType.ORDERS_REPORT,
      DataSourceType.ORDER_TASKS_REPORT,
      DataSourceType.ORDERS_WITH_TASKS_REPORT,
      DataSourceType.LINE_ITEM,
      DataSourceType.LINE_ITEMS_REPORT,
      DataSourceType.LINE_ITEM_TASKS_REPORT,
      DataSourceType.LINE_ITEMS_WITH_TASKS_REPORT,
      DataSourceType.FLIGHTS_REPORT,
      DataSourceType.FLIGHT_TASKS_REPORT,
      DataSourceType.FLIGHTS_WITH_TASKS_REPORT,
      DataSourceType.TASK,
      DataSourceType.MASTER,
      DataSourceType.EC_TASKS_SUMMARY,
      DataSourceType.BUY_SHEET,
    ].includes(this.type);
  }

  isLeadsDataSourceType() {
    return this.type === DataSourceType.LEADS;
  }

  isGoalDataSourceType() {
    return this.type === DataSourceType.GOAL_DATA;
  }
}
